import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import { useTranslation } from "react-i18next";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";

// Dashboard-only transactions table backed by GET /Dashboard/{id}/Transactions.
// The original TransactionsTable is still used by the customer details page.

interface Transaction {
  id: string;
  description: string;
  customerId: string;
  customerName: string;
  point: number;
  type: "notification" | "point" | "reward" | "signup" | "deduct";
  createdAt: string;
}

interface TableProps {
  passTemplateId: string;
  from?: string;
  to?: string;
}

const typeBadge = (type: string, label: string) => {
  const styles: Record<string, string> = {
    point:        "bg-brand-50 text-brand-700",
    reward:       "bg-emerald-50 text-emerald-700",
    signup:       "bg-violet-50 text-violet-700",
    notification: "bg-amber-50 text-amber-700",
    deduct:       "bg-red-50 text-red-700",
  };
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[type] ?? "bg-gray-100 text-gray-600"}`}>
      {label}
    </span>
  );
};

const PAGE_SIZE = 20;

const TransactionsTableEfficient: React.FC<TableProps> = ({ passTemplateId, from, to }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { t } = useTranslation();

  // A new range starts again from the first page
  useEffect(() => {
    setPage(1);
  }, [from, to]);

  useEffect(() => {
    let cancelled = false;
    const fetchTransactions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await OrganizationAxiosInstance.get<Transaction[]>(
          `/Dashboard/${passTemplateId}/Transactions`,
          { params: { page, pageSize: PAGE_SIZE, from, to } }
        );
        if (cancelled) return;
        setTransactions(response.data);
        setTotalCount(parseInt(response.headers["x-total-count"] ?? "0"));
      } catch {
        if (!cancelled) setError(t("failedToLoad"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTransactions();
    return () => { cancelled = true; };
  }, [page, passTemplateId, from, to, t]);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "notification": return t("sentNotificationToCustomerType");
      case "point":        return t("pointsEacrned");
      case "reward":       return t("rewardRadeemedType");
      case "signup":       return t("signedUpType");
      case "deduct":       return t("deductType");
      default:             return type;
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const customerLink = (tx: Transaction) => `/Dashboard/Program/${passTemplateId}/Customer/${tx.customerId}`;

  if (loading && transactions.length === 0) return <p className="px-6 py-8 text-sm text-gray-400 text-center">Loading...</p>;
  if (error) return <p className="px-6 py-8 text-sm text-gray-400 text-center">{error}</p>;
  if (transactions.length === 0) return <p className="px-6 py-8 text-sm text-gray-400 text-center">{t("noTransactionsInRange")}</p>;

  return (
    <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
      {/* Mobile */}
      <div className="sm:hidden divide-y divide-gray-100">
        {transactions.map(tx => (
          <div key={tx.id} className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              {typeBadge(tx.type, getTypeLabel(tx.type))}
              <span className={`text-sm font-bold ${tx.point >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                {tx.point >= 0 ? `+${tx.point}` : tx.point}
              </span>
            </div>
            <p className="text-sm text-gray-800">{tx.description}</p>
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>{new Date(tx.createdAt).toLocaleDateString()}</span>
              <Link to={customerLink(tx)} className="text-brand-600 font-medium hover:underline truncate max-w-[50%]">
                {tx.customerName || t("customerDetails")}
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop */}
      <table className="hidden sm:table min-w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("date")}</th>
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("customer")}</th>
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("description")}</th>
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("type")}</th>
            <th className="px-6 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider">{t("points")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {transactions.map(tx => (
            <tr key={tx.id} className="hover:bg-brand-50/40 transition">
              <td className="px-6 py-4 text-xs text-gray-500 whitespace-nowrap">
                {new Date(tx.createdAt).toLocaleString("en-US", {
                  year: "numeric", month: "short", day: "numeric",
                  hour: "2-digit", minute: "2-digit", hour12: true,
                })}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Link to={customerLink(tx)} className="text-sm text-brand-600 hover:underline font-medium">
                  {tx.customerName || t("customerDetails")}
                </Link>
              </td>
              <td className="px-6 py-4 text-sm text-gray-800">{tx.description}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                {typeBadge(tx.type, getTypeLabel(tx.type))}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`text-sm font-bold ${tx.point >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {tx.point >= 0 ? `+${tx.point}` : tx.point}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <HiChevronLeft className="w-4 h-4 rtl:rotate-180" />
          </button>
          <span className="text-sm text-gray-500">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
            className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-brand-50 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <HiChevronRight className="w-4 h-4 rtl:rotate-180" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionsTableEfficient;
